/**
 * ProjectDashboard Page
 * AI-powered project overview with insights, decisions, progress, and risks.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Lightbulb, ListChecks, HelpCircle, AlertTriangle, Sparkles, Crown, ArrowRight } from 'lucide-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { useCapabilities } from '@/contexts/CapabilitiesContext';
import { useProjectAnalysis } from '@/hooks/useProjectAnalysis';
import { PageLayout } from '@/components';
import { AppPageHeader, UnauthenticatedPage } from '@/components/ui';
import { Chrome } from '@/components/layout';
import { AnalysisHeader } from './components/AnalysisHeader';
import { InsightsSummary } from './components/InsightsSummary';
import { DecisionsList } from './components/DecisionsList';
import { ProgressTracker } from './components/ProgressTracker';
import { OpenQuestionsList } from './components/OpenQuestionsList';
import { RisksList } from './components/RisksList';

type TabType = 'decisions' | 'progress' | 'questions' | 'risks';

export const ProjectDashboard: React.FC = () => {
  const { t } = useTranslation();

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'decisions', label: t('dashboard.tabDecisions'), icon: <Lightbulb className="w-4 h-4" /> },
    { id: 'progress', label: t('dashboard.tabProgress'), icon: <ListChecks className="w-4 h-4" /> },
    { id: 'questions', label: t('dashboard.tabQuestions'), icon: <HelpCircle className="w-4 h-4" /> },
    { id: 'risks', label: t('dashboard.tabRisks'), icon: <AlertTriangle className="w-4 h-4" /> },
  ];

  const { isAuthenticated, isLoading: authLoading, appMode } = useAuthContext();
  const { hasCapability } = useCapabilities();
  const {
    analysis,
    isLoading,
    isRefreshing,
    isStale,
    error,
    lastUpdated,
    documentCount,
    refresh,
  } = useProjectAnalysis();

  const [activeTab, setActiveTab] = useState<TabType>('decisions');

  // Auth loading
  if (authLoading) {
    return (
      <PageLayout>
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        </div>
      </PageLayout>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <UnauthenticatedPage
        icon={<Sparkles className="w-8 h-8 text-indigo-400" />}
        title={t('dashboard.unauthTitle')}
        description={t('dashboard.unauthDescription')}
      />
    );
  }

  // Pro feature gate - show upgrade prompt for users without insight_recommendations
  if (!hasCapability('insight_recommendations')) {
    return (
      <PageLayout variant="plain">
        <Chrome component="appPageHeader">
          <AppPageHeader
            icon={<Lightbulb className="w-4 h-4" />}
            badge={t('dashboard.badge')}
            subtitle={t('dashboard.subtitle')}
          />
        </Chrome>
        <div className="max-w-3xl mx-auto px-6 py-8 bg-gradient-to-b from-slate-50 via-white to-indigo-50/30">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center">
              <Crown className="w-10 h-10 text-amber-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">
              {t('dashboard.unlockInsights')}
            </h2>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              {t('dashboard.unlockDescription')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/upgrade"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl font-semibold transition-all shadow-lg shadow-amber-500/25"
              >
                <Crown className="w-5 h-5" />
                {t('dashboard.upgradeToPro')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-8 pt-6 border-t border-slate-100">
              <p className="text-sm text-slate-500 mb-4">{t('dashboard.proFeaturesInclude')}</p>
              <div className="flex flex-wrap justify-center gap-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-full text-sm">
                  <Lightbulb className="w-4 h-4" />
                  {t('dashboard.keyDecisions')}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm">
                  <ListChecks className="w-4 h-4" />
                  {t('dashboard.progressTracking')}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm">
                  <HelpCircle className="w-4 h-4" />
                  {t('dashboard.openQuestions')}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {t('dashboard.riskDetection')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Loading analysis
  if (isLoading) {
    return (
      <PageLayout variant="plain">
        <Chrome component="appPageHeader">
          <AppPageHeader
            icon={<Lightbulb className="w-4 h-4" />}
            badge={t('dashboard.badge')}
            subtitle={t('dashboard.subtitle')}
          />
        </Chrome>
        <section className="py-12 bg-gradient-to-b from-slate-50 via-white to-indigo-50/30 min-h-[50vh]">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mr-3" />
              <span className="text-slate-600">{t('dashboard.loadingAnalysis')}</span>
            </div>
          </div>
        </section>
      </PageLayout>
    );
  }

  // Error state
  if (error && !analysis) {
    return (
      <PageLayout variant="plain">
        <Chrome component="appPageHeader">
          <AppPageHeader
            icon={<Lightbulb className="w-4 h-4" />}
            badge={t('dashboard.badge')}
            subtitle={t('dashboard.subtitle')}
          />
        </Chrome>
        <section className="py-12 bg-gradient-to-b from-slate-50 via-white to-indigo-50/30 min-h-[50vh]">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center py-16">
              <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-red-400" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">{t('dashboard.unableToLoadAnalysis')}</h2>
              <p className="text-slate-600 mb-6">{error}</p>
              <button
                onClick={refresh}
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
              >
                {t('dashboard.tryAgain')}
              </button>
            </div>
          </div>
        </section>
      </PageLayout>
    );
  }

  // No analysis yet - show first-time prompt
  if (!analysis) {
    return (
      <PageLayout variant="plain">
        <Chrome component="appPageHeader">
          <AppPageHeader
            icon={<Lightbulb className="w-4 h-4" />}
            badge={t('dashboard.badge')}
            subtitle={t('dashboard.subtitle')}
          />
        </Chrome>
        <section className="py-12 bg-gradient-to-b from-slate-50 via-white to-indigo-50/30 min-h-[50vh]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center py-16">
            <Sparkles className="w-16 h-16 mx-auto mb-4 text-indigo-400" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('dashboard.analyzeYourProject')}</h2>
            <p className="text-slate-600 mb-2">
              {t('dashboard.analyzeDescription', { count: documentCount })}
            </p>
            <p className="text-sm text-slate-500 mb-6">
              {t('dashboard.analyzeSubtext')}
            </p>
            <button
              onClick={refresh}
              disabled={isRefreshing}
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                isRefreshing
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {isRefreshing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('dashboard.analyzing')}
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  {t('dashboard.runAnalysis')}
                </>
              )}
            </button>
          </div>
        </div>
        </section>
      </PageLayout>
    );
  }

  // Main dashboard view
  const insights = analysis.insights;

  return (
    <PageLayout variant="plain">
      <Chrome component="appPageHeader">
        <AppPageHeader
          icon={<Lightbulb className="w-4 h-4" />}
          badge={t('dashboard.badge')}
          title={t('dashboard.title')}
          subtitle={t('dashboard.subtitle')}
        />
      </Chrome>
      <section className="py-6 sm:py-8 bg-gradient-to-b from-slate-50 via-white to-indigo-50/30 min-h-[50vh]">
      <div className="max-w-5xl mx-auto px-6 py-4">
        {/* Analysis Header */}
        <AnalysisHeader
          analyzedAt={lastUpdated}
          isStale={isStale}
          documentCount={documentCount}
          isRefreshing={isRefreshing}
          onRefresh={refresh}
        />

        {/* Summary */}
        <InsightsSummary
          summary={insights.summary}
          metrics={insights.metrics}
        />

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {/* Tab navigation */}
          <div className="flex border-b border-slate-200">
            {tabs.map((tab) => {
              const count =
                tab.id === 'decisions'
                  ? insights.keyDecisions.length
                  : tab.id === 'progress'
                  ? insights.progress.length
                  : tab.id === 'questions'
                  ? insights.openQuestions.length
                  : insights.risks.length;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  {tab.icon}
                  <span className="hidden sm:inline">{tab.label}</span>
                  {count > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                      activeTab === tab.id
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="p-6">
            {activeTab === 'decisions' && (
              <DecisionsList decisions={insights.keyDecisions} />
            )}
            {activeTab === 'progress' && (
              <ProgressTracker items={insights.progress} />
            )}
            {activeTab === 'questions' && (
              <OpenQuestionsList questions={insights.openQuestions} />
            )}
            {activeTab === 'risks' && (
              <RisksList risks={insights.risks} />
            )}
          </div>
        </div>

        {/* Error banner if any */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>
      </section>
    </PageLayout>
  );
};

export default ProjectDashboard;
